import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Clock, Mail, Phone, ArrowRight } from 'lucide-react';

const RegistrationSuccess = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {/* Success Icon */}
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>

        {/* Success Message */}
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          Registration Submitted Successfully!
        </h1>
        
        <p className="text-gray-600 mb-8">
          Thank you for registering as a seller on our construction marketplace. Your application has been submitted and is now under review.
        </p>

        {/* Status Steps */}
        <div className="space-y-4 mb-8">
          <div className="flex items-center text-left">
            <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-900">Registration Completed</p>
              <p className="text-xs text-gray-500">All required information submitted</p>
            </div>
          </div>

          <div className="flex items-center text-left">
            <div className="flex-shrink-0 w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-900">Under Review</p>
              <p className="text-xs text-gray-500">Our team is verifying your documents</p>
            </div>
          </div>

          <div className="flex items-center text-left opacity-50">
            <div className="flex-shrink-0 w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-900">Approval</p>
              <p className="text-xs text-gray-500">Account activation and access granted</p>
            </div>
          </div>
        </div>

        {/* What's Next */}
        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">What happens next?</h3>
          <ul className="text-xs text-blue-800 space-y-1 text-left">
            <li>• Our team will review your documents within 2-3 business days</li>
            <li>• You'll receive an email notification about the approval status</li>
            <li>• Once approved, you can start listing your products</li>
            <li>• You'll get access to the seller dashboard and analytics</li>
          </ul>
        </div>

        {/* Contact Information */}
        <div className="border-t border-gray-200 pt-6">
          <p className="text-sm text-gray-600 mb-4">
            Need help or have questions?
          </p>
          <div className="flex justify-center space-x-6">
            <a 
              href="mailto:support@buildify.com" 
              className="flex items-center text-sm text-blue-600 hover:text-blue-800"
            >
              <Mail className="w-4 h-4 mr-1" />
              Email Support
            </a>
            <a 
              href="tel:+911234567890" 
              className="flex items-center text-sm text-blue-600 hover:text-blue-800"
            >
              <Phone className="w-4 h-4 mr-1" />
              Call Us
            </a>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-3 mt-8">
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            Go to Login
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            Back to Homepage
          </button>
        </div>

        {/* Reference Number */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Reference ID: REG-{Date.now().toString().slice(-8)}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Keep this reference for future correspondence
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegistrationSuccess;
